import React, { createContext, useState, useContext, useEffect } from 'react';

const GlobalStateContext = createContext();

export function GlobalStateProvider({ children }) {
  const [domainUrl, setDomainUrl] = useState(null);
  const [agentId, setAgentId] = useState(null);
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);

  useEffect(() => {
    // Function to handle messages from parent window (when app is in iframe)
    const handleMessage = (event) => {
      // For security, you might want to check event.origin here
      // if (event.origin !== 'https://your-trusted-domain.com') return;
      
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        
        if (data && (data.domain || data.agent_id)) {

          if (data.domain) {
            setDomainUrl(data.domain);
          }
          
          if (data.agent_id) {
            setAgentId(data.agent_id);
          }
          
          setIsIframeLoaded(true);
        }
      } catch (error) {
        console.error('Error parsing message from parent:', error);
      }
    };

    // Check if we're in an iframe
    const isInIframe = window.self !== window.top;
    
    if (isInIframe) {
      // Listen for messages from parent window
      window.addEventListener('message', handleMessage);
      
      // Send a ready message to parent to indicate we're ready to receive data
      window.parent.postMessage({ type: 'iframe_ready' }, '*');
      
    } else {
      // Not in iframe, set default values for development
      setDomainUrl('http://localhost:3000');
      setAgentId('146'); // Default agent ID for development
      setIsIframeLoaded(true);
    }

    // Cleanup
    return () => {
      if (isInIframe) {
        window.removeEventListener('message', handleMessage);
      }
    };
  }, []);

  // Utility function to construct voice wizard URL
  const constructVoiceWizardUrl = (promptId, phoneNumber, isInitialPrompt = false) => {
    if (!domainUrl || !agentId || !promptId || !phoneNumber) {
      console.warn('Missing required parameters for voice wizard URL:', {
        domainUrl, agentId, promptId, phoneNumber
      });
      return null;
    }

    // Ensure domain URL ends with /
    const baseUrl = domainUrl.endsWith('/') ? domainUrl : domainUrl + '/';
    
    // Construct the URL
    // For initial prompts: /voice_wizard/370?agent_id=146&number=t35a69aa7f9
    // For subprompts: /voice_wizard/370
    if (isInitialPrompt) {
      return `${baseUrl}voice_wizard/${promptId}?agent_id=${agentId}&number=${phoneNumber}`;
    } else {
      return `${baseUrl}voice_wizard/${promptId}`;
    }
  };

  const value = {
    domainUrl,
    agentId,
    isIframeLoaded,
    constructVoiceWizardUrl,
    setDomainUrl, // Allow manual setting if needed
    setAgentId // Allow manual setting if needed
  };

  return (
    <GlobalStateContext.Provider value={value}>
      {children}
    </GlobalStateContext.Provider>
  );
}

export function useGlobalState() {
  const context = useContext(GlobalStateContext);
  if (context === undefined) {
    throw new Error('useGlobalState must be used within a GlobalStateProvider');
  }
  return context;
} 